/**
 * Formate un numéro de téléphone au format français
 * @param phone - Le numéro de téléphone à formater
 * @returns Le numéro formaté
 */
function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';
  
  // Nettoyer le numéro (garder seulement les chiffres et le +)
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Si le numéro commence par +33
  if (cleaned.startsWith('+33')) {
    const digits = cleaned.slice(3);
    if (digits.length === 9) {
      return `+33 ${digits[0]} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
    }
    return cleaned;
  }
  
  // Si le numéro commence par 0033
  if (cleaned.startsWith('0033')) {
    const digits = cleaned.slice(4);
    if (digits.length === 9) {
      return `+33 ${digits[0]} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
    }
    return cleaned;
  }
  
  // Format français standard (10 chiffres)
  if (cleaned.length === 10 && cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
  }
  
  // Numéro à 9 chiffres : probablement il manque le 0 au début
  if (cleaned.length === 9 && !cleaned.startsWith('0')) {
    const withZero = '0' + cleaned;
    return `${withZero.slice(0, 2)} ${withZero.slice(2, 4)} ${withZero.slice(4, 6)} ${withZero.slice(6, 8)} ${withZero.slice(8)}`;
  }
  
  // Numéro à 8 chiffres sans 0 au début : probablement il manque "0X" au début
  // On essaie de deviner : si commence par 6 ou 7, c'est un mobile (06 ou 07)
  if (cleaned.length === 8 && !cleaned.startsWith('0')) {
    const firstDigit = cleaned[0];
    if (firstDigit === '6' || firstDigit === '7') {
      const withZero = '0' + cleaned;
      return `${withZero.slice(0, 2)} ${withZero.slice(2, 4)} ${withZero.slice(4, 6)} ${withZero.slice(6, 8)} ${withZero.slice(8)}`;
    }
  }
  
  // Si le numéro a entre 7 et 10 chiffres mais ne correspond à aucun format
  // On essaie de le formater en groupes de 2
  if (cleaned.length >= 7 && cleaned.length <= 10) {
    const parts = [];
    for (let i = 0; i < cleaned.length; i += 2) {
      parts.push(cleaned.slice(i, i + 2));
    }
    return parts.join(' ');
  }
  
  // Sinon retourner tel quel
  return phone;
}

/**
 * Formate une date au format français
 * @param date - La date à formater (string ISO, Date, ou timestamp)
 * @returns La date formatée au format JJ/MM/AAAA
 */
function formatDate(date: any): string {
  if (!date) return '';
  
  try {
    let dateObj: Date;
    
    // Si c'est déjà une Date
    if (date instanceof Date) {
      dateObj = date;
    }
    // Si c'est une chaîne ISO ou un timestamp
    else {
      dateObj = new Date(date);
    }
    
    // Vérifier que la date est valide
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    // Formater au format JJ/MM/AAAA
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    return '';
  }
}

/**
 * Convertit un tableau d'objets en format CSV
 * @param data - Les données à convertir
 * @param headers - Les en-têtes du CSV avec leurs clés
 * @returns Une chaîne de caractères au format CSV
 */
export function convertToCSV<T extends Record<string, any>>(
  data: T[],
  headers: { key: keyof T | string; label: string; type?: 'date' | 'phone' | 'boolean' }[]
): string {
  // Créer la ligne d'en-têtes
  const headerLine = headers.map(h => `"${h.label}"`).join(';');
  
  // Créer les lignes de données
  const dataLines = data.map(item => {
    return headers.map(header => {
      // Récupérer la valeur (supporte les chemins imbriqués comme "address.city")
      const keys = String(header.key).split('.');
      let value: any = item;
      
      for (const key of keys) {
        value = value?.[key];
      }
      
      // Traiter la valeur selon son type
      if (value === null || value === undefined) {
        return '""';
      }
      
      // Type spécifique défini dans le header
      if (header.type === 'phone') {
        return `"${formatPhoneNumber(value)}"`;
      }
      
      if (header.type === 'date') {
        return `"${formatDate(value)}"`;
      }
      
      if (header.type === 'boolean' || typeof value === 'boolean') {
        return `"${value ? 'Oui' : 'Non'}"`;
      }
      
      // Convertir les dates (détection automatique)
      if (value instanceof Date) {
        return `"${formatDate(value)}"`;
      }
      
      // Échapper les guillemets dans les chaînes
      const stringValue = String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    }).join(';');
  });
  
  // Combiner en-têtes et données
  return [headerLine, ...dataLines].join('\n');
}

/**
 * Télécharge un fichier CSV
 * @param csvContent - Le contenu CSV
 * @param filename - Le nom du fichier (sans extension)
 */
export function downloadCSV(csvContent: string, filename: string): void {
  // Ajouter le BOM UTF-8 pour que Excel reconnaisse l'encodage
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  
  // Nettoyer
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 0);
}

